import { motion } from "framer-motion";
import { Camera, FileText, MapPin, Send } from "lucide-react";
import { BrowserFrame, PhoneFrame } from "@/components/home/Frames";
import { PayrollScreens } from "@/components/home/screens/PayrollScreens";
import { Clock } from "@/components/home/screens/PhoneScreens";
import { SAMPLE_REPORT } from "@/components/home/ProofOfWork";

const Chip = ({ icon: Icon, children, className }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: [0, -6, 0] }}
    transition={{ opacity: { delay: 0.9 }, y: { delay: 0.9, duration: 5, repeat: Infinity, ease: "easeInOut" } }}
    className={`absolute hidden md:flex items-center gap-2 rounded-xl bg-white/95 text-[#0F172A] px-3 py-2 text-xs font-semibold shadow-2xl ${className}`}>
    <span className="w-7 h-7 rounded-lg bg-[#EA580C]/10 text-[#EA580C] grid place-items-center"><Icon className="w-4 h-4" /></span>{children}
  </motion.div>
);

const Timesheet = () => (
  <>
    <BrowserFrame testid="landing-visual-browser"><PayrollScreens step={0} /></BrowserFrame>
    <div className="hidden sm:block absolute -bottom-16 -right-3 lg:-right-8 w-[150px] lg:w-[165px]"><PhoneFrame><Clock /></PhoneFrame></div>
    <Chip icon={MapPin} className="-top-5 left-10">Jack clocked on at Lot 12 · 6:48am</Chip>
  </>
);

const Payroll = () => (
  <>
    <BrowserFrame testid="landing-visual-browser"><PayrollScreens step={2} /></BrowserFrame>
    <Chip icon={Send} className="-bottom-6 left-6">Payroll PDF sent to your bookkeeper</Chip>
  </>
);

const PHOTOS = [["/demo/issue_ceiling.jpg", "Issue · 8:30am"], ["/demo/after_room3.jpg", "Completed · 12:58pm"]];
const Proof = () => (
  <div className="relative grid grid-cols-[1.1fr_1fr] gap-4 items-center">
    <a href={SAMPLE_REPORT} target="_blank" rel="noreferrer" data-testid="landing-visual-report" className="block -rotate-2 hover:rotate-0 transition-transform duration-300">
      <img src="/sample-job-report.png" alt="Sample proof of job report with times on site and photos" className="w-full rounded-md shadow-2xl ring-1 ring-white/10" />
    </a>
    <div className="grid gap-4">
      {PHOTOS.map(([src, cap]) => (
        <figure key={src} className="rounded-xl overflow-hidden bg-white shadow-2xl">
          <img src={src} alt={cap} className="w-full aspect-[4/3] object-cover" />
          <figcaption className="px-3 py-2 text-xs font-semibold text-[#0F172A] flex items-center gap-1.5"><Camera className="w-3.5 h-3.5 text-[#EA580C]" />{cap}</figcaption>
        </figure>
      ))}
    </div>
    <Chip icon={FileText} className="-top-5 left-6">Report emailed to the client · 1:26pm</Chip>
  </div>
);

// Industry pages: a real site photo with the job card, before/after shots and a time-stamp chip on top.
const Industry = ({ media }) => (
  <div className="relative">
    <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 aspect-[4/3]">
      <img src={media.hero} alt={media.heroAlt} className="w-full h-full object-cover" data-testid="landing-visual-hero" />
    </div>
    <div className="absolute left-4 top-4 rounded-xl bg-[#0B1120]/90 backdrop-blur px-4 py-3 text-white shadow-xl" data-testid="landing-visual-job">
      <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#EA580C]">Today&apos;s job</p>
      <p className="mt-1 text-sm font-semibold">{media.job}</p>
    </div>
    <div className="absolute -bottom-10 right-3 sm:-right-4 grid grid-cols-2 gap-3 w-[78%] sm:w-[68%]">
      {[media.before, media.after].map(([src, cap]) => (
        <figure key={src} className="rounded-xl overflow-hidden bg-white shadow-2xl">
          <img src={src} alt={cap} className="w-full aspect-[4/3] object-cover" />
          <figcaption className="px-3 py-2 text-[11px] sm:text-xs font-semibold text-[#0F172A] flex items-center gap-1.5"><Camera className="w-3.5 h-3.5 text-[#EA580C]" />{cap}</figcaption>
        </figure>
      ))}
    </div>
    <Chip icon={MapPin} className="-top-5 right-6">{media.chip}</Chip>
  </div>
);

const VISUALS = { timesheet: Timesheet, payroll: Payroll, proof: Proof, industry: Industry };

export const LandingVisual = ({ page }) => {
  const V = VISUALS[page.visual];
  return <V media={page.media} />;
};
